import { Html, useGLTF } from '@react-three/drei';
import { useEffect, useMemo, useRef } from 'react';
import { Color, MeshStandardMaterial } from 'three';
import { ACITVE_EMISSIVE_INTENSITY, ACTIVE_EMISSIVE_COLOR } from 'variables/consts.js';
import gsap from 'gsap';
import { useRecoilValue } from 'recoil';
import { ParentModeAtom } from 'modules/manager/index.js';
import { useFrame } from '@react-three/fiber';

useGLTF.preload('/assets/models/icons/beacon_icon/beacon_icon.glb');

export const BeaconIcon = (props) => {
  const {
    onClick,
    scale = [1, 1, 1],
    position,
    drawingFloorOffsetY,
    hoverScaleFactor = 1.6,
    isActivated = false,
    isHovered = false,
    beacon,
    setIconRef,
    ...rest
  } = props;

  const iconRef = useRef();
  const textRef = useRef();

  const parentMode = useRecoilValue(ParentModeAtom);

  const { scene, nodes, materials } = useGLTF('/assets/models/icons/beacon_icon/beacon_icon.glb');

  const glowMaterial = useMemo(() => {
    const material = new MeshStandardMaterial();

    if (materials.color) {
      material.color = materials.color.color;
      material.roughness = materials.color.roughness || 0.3;
      material.metalness = materials.color.metalness || 0.5;
    }

    material.emissive = new Color(ACTIVE_EMISSIVE_COLOR);
    material.emissiveIntensity = ACITVE_EMISSIVE_INTENSITY;
    material.toneMapped = false;

    return material;
  }, [materials.color]);

  const handleClick = (e) => {
    e.stopPropagation();

    if (!iconRef.current) return;

    const timeline = gsap.timeline();
    const originalY = iconRef.current.position.y;

    timeline
      .to(
        iconRef.current.position,
        {
          y: originalY + 0.5,
          duration: 0.35,
          ease: 'power2.out'
        },
        0
      )
      .to(
        iconRef.current.scale,
        {
          x: scale * hoverScaleFactor,
          y: scale * hoverScaleFactor,
          z: scale * hoverScaleFactor,
          duration: 0.7,
          ease: 'power2.out'
        },
        0
      )
      .to(
        iconRef.current.position,
        {
          y: originalY,
          duration: 0.2,
          ease: 'power1.inOut'
        },
        '>-0.2'
      )
      .to(
        iconRef.current.scale,
        {
          x: scale,
          y: scale,
          z: scale,
          duration: 0.2,
          ease: 'power1.inOut'
        },
        '>-0.2'
      );

    onClick?.(e);
  };

  useEffect(() => {
    if (iconRef.current && beacon.id && setIconRef) {
      setIconRef(beacon.id, iconRef.current);
    }
  }, [beacon.id, setIconRef]);

  useEffect(() => {
    scene.traverse((mesh) => {
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });

    if (!iconRef.current) return;

    const rotationTween = gsap.to(iconRef.current.rotation, {
      duration: 3,
      repeat: -1,
      yoyo: false,
      ease: 'none',
      y: Math.PI * 2
    });

    return () => {
      rotationTween.kill();
    };
  }, []);

  useEffect(() => {
    if (!iconRef.current) return;

    if (isHovered) {
      /**
       * fileName       : RoadSign/index.jsx
       * author         : KDONG1224
       * date           : 2025-04-07
       * description    : 더미체계로 모델링 변경이 되어 모델링에 따라 마커 호버시 Y축으로 이동하는 값이 달라짐
       *                  모델링 축척이 변경된다면, 여기서도 수정이 필요함
       *                  (현재는 S003 - 제일건설 곤지암 현장에 맞춰서 수정됨)
       * ===========================================================
       * DATE              AUTHOR             NOTE
       * -----------------------------------------------------------
       * 2025-04-07        KDONG1224       더미체계로 모델링 변경으로 인한 수정
       */
      gsap.to(iconRef.current.position, {
        y: position[1] + drawingFloorOffsetY / 2,
        duration: 0.5,
        ease: 'power2.out'
      });

      gsap.to(iconRef.current.scale, {
        x: scale * hoverScaleFactor,
        y: scale * hoverScaleFactor,
        z: scale * hoverScaleFactor,
        duration: 0.5,
        ease: 'power2.out'
      });
    } else {
      gsap.to(iconRef.current.position, {
        y: position[1],
        duration: 0.5,
        ease: 'power2.out'
      });

      gsap.to(iconRef.current.scale, {
        x: scale,
        y: scale,
        z: scale,
        duration: 0.5,
        ease: 'power2.out'
      });
    }
  }, [isHovered, position]);

  useFrame(({ camera }) => {
    textRef.current?.lookAt(camera.position);
  });

  return (
    <group
      {...rest}
      dispose={null}
      ref={iconRef}
      scale={scale}
      position={position}
      onPointerDown={handleClick}
      renderOrder={9999}
    >
      <Html
        zIndexRange={[100, 0]}
        position={[1, 100, 1]}
        occlude={'raycast'}
        center
        style={{
          userSelect: 'none',
          width: 'max-content',
          padding: '4px 8px',
          background: 'rgba(255,255,255,0.9)',
          borderRadius: '12px'
        }}
      >
        <span className="beacon-info">{beacon.minor && beacon.minor.toString()}</span>{' '}
        {beacon.beaconType === 'S' && <span style={{ color: 'red' }}>{beacon.risk} </span>}
        {parentMode === 'retrieve' && (
          <span style={{ color: '#1890ff' }}>({beacon?.workers.length}명)</span>
        )}
      </Html>
      <mesh
        geometry={nodes.Beacon_icon.geometry}
        material={isActivated ? glowMaterial : nodes.Beacon_icon.material}
      ></mesh>
    </group>
  );
};
